import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  DashboardIntegrationsListFactory,
  IntegrationDisconnectParams,
  IntegrationKeyType,
} from 'src/dashboard/integrations/list-factory';
import { DashboardIntegrationsApiKeyPanel } from 'src/dashboard/integrations/api-key-panel';
import { LeftStrongIcon } from 'src/monet/icons';
import { Nullable } from 'src/types/nullable.type';
import { enterpriseGatedIntegrations, integrations } from 'src/utils/integration/providers';
import {
  apiDeleteUserApiKey,
  apiGetOrganizationParagonToken,
  apiGetUserAccessTokens,
  apiGetUserApiKeys,
  apiRevokeAccessToken,
} from 'src/utils/journeyApi';
import { openPopupWindow } from 'src/utils/window';
import { IntegrationApiKey } from 'src/common/interfaces/integration-api-key.interface';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { ErrorInterface } from 'src/common/interfaces/error.interface';
import OrganizationLink from 'src/common/OrganizationLink';
import { IconButton } from 'src/common/icon-button';
import { useParams } from 'react-router-dom';
import IntegrationDetail from 'src/dashboard/integrations/detail';
import { useSlideOverStore } from 'src/store/slide-over';
import { useCurrentOrganization } from 'src/store/organization';
import { useCurrentUser } from 'src/store/user';
import { OrganizationUser } from 'src/common/interfaces/organization/user.interface';
import { useCurrentSubscription } from 'src/store/subscription';
import PaywallDetail from 'src/dashboard/integrations/paywall-detail';
import { useParagon } from 'src/utils/use-paragon.hook';
import { useMount } from 'react-use';

export const PageIntegrationDetail = () => {
  const { code } = useParams();
  const { setSuccessNotification, setErrorNotification } = useNotificationMessage();
  const openSlideOver = useSlideOverStore((state) => state.openSlideOver);
  const [isLowerPlanGated, setLowerPlanGated] = useState(false);
  const { isPro, isEnterprise, isOnTrial, subscriptionLoading } = useCurrentSubscription((state) => state);
  const isFetchingOnProgressRef = useRef(false);
  const integrationProvider = useMemo(() => integrations.find((integration) => integration.code === code)!, [code]);
  // @author @anilsevim
  // the reason for this is because we have both hubspot-tracker and hubspot
  // all hubspot tracker integrations are still under the hubspot code
  // we eventually need to migrate them to hubspot tracker and remove this parentCode logic
  // but until then this part should stay
  const maybeParentIntegrationCode = integrationProvider.parentCode || integrationProvider.code;

  const [isLoaded, setIsLoaded] = useState<boolean>(!!integrationProvider.is_static);

  const [integratedKeys, setIntegratedKeys] = useState<Nullable<IntegrationKeyType[]>>(null);
  const [showApiKeyPanel, setShowApiKeyPanel] = useState(false);

  const currentOrganization = useCurrentOrganization((state) => state.currentOrganization);
  const currentUser = useCurrentUser((state) => state.currentUser) as OrganizationUser;

  const paragon = useParagon(currentUser!, currentOrganization);

  const fetchIntegrations = useCallback(() => {
    const { is_api_key, is_oauth } = integrationProvider;
    setIsLoaded(false);

    const integrationFetcher = is_api_key
      ? apiGetUserApiKeys(maybeParentIntegrationCode)
      : is_oauth
      ? apiGetUserAccessTokens(maybeParentIntegrationCode)
      : apiGetOrganizationParagonToken();

    integrationFetcher.then(async ({ tokens, api_keys, paragon_token }) => {
      if (is_oauth || is_api_key) {
        setIsLoaded(true);
        setIntegratedKeys(tokens || api_keys);
      } else if (paragon_token) {
        paragon.initialize().then(() => {
          paragon.authenticate().then(() => {
            setIsLoaded(true);
            setIntegratedKeys([]);
          });
        });
      }
    });
  }, [maybeParentIntegrationCode, integrationProvider, currentUser, currentOrganization]);

  useMount(() => {
    if (!isLoaded) {
      fetchIntegrations();
    }
  });

  useEffect(() => {
    // Only check paywall gating after subscription data has been loaded
    if (!subscriptionLoading && enterpriseGatedIntegrations.includes(maybeParentIntegrationCode) && !isEnterprise && !isOnTrial) {
      setLowerPlanGated(true);
    } else if (!subscriptionLoading) {
      setLowerPlanGated(false);
    }
  }, [maybeParentIntegrationCode, isPro, isEnterprise, isOnTrial, subscriptionLoading]);

  const onDisconnect = useCallback(
    async ({ name, stack, type }: IntegrationDisconnectParams<IntegrationKeyType>) => {
      try {
        await (type === 'oauth' ? apiRevokeAccessToken(stack.provider, stack.id) : apiDeleteUserApiKey(stack.id));
        if (integratedKeys) {
          setIntegratedKeys(integratedKeys.filter((integration) => integration.id !== stack.id));
        }
        setSuccessNotification(`${name} successfully disconnected`);
      } catch (err) {
        setErrorNotification((err as ErrorInterface).message);
      }
    },
    [integratedKeys, setErrorNotification, setSuccessNotification]
  );
  const IntegrationKeyList = DashboardIntegrationsListFactory(integrationProvider, onDisconnect);

  const isParagonIntegrationEnabled: boolean = useMemo(() => {
    if (!integratedKeys || !code || !window.paragon) return false;

    const paragonUser = window.paragon.getUser();

    return paragonUser && paragonUser.authenticated && paragonUser.integrations[code]?.enabled;
  }, [code, integratedKeys]);

  const hasApiKey = Boolean(
    integrationProvider.is_static || isParagonIntegrationEnabled || (integratedKeys && integratedKeys.length > 0)
  );

  const onParagonConfigureClicked = useCallback(() => {
    window.paragon.connect(code);
  }, [code]);

  const apiKeyList = useMemo(() => {
    return hasApiKey ? (
      <IntegrationKeyList
        keys={integratedKeys || []}
        isParagonAuthenticated={isParagonIntegrationEnabled}
        onParagonConfigureClick={onParagonConfigureClicked}
      />
    ) : null;
  }, [IntegrationKeyList, hasApiKey, integratedKeys, isParagonIntegrationEnabled, onParagonConfigureClicked]);

  const onApiKeyCreated = useCallback(
    (stack: IntegrationApiKey) => {
      if (integratedKeys !== null) {
        setIntegratedKeys([...integratedKeys, stack]);
      }
    },
    [integratedKeys]
  );

  const onConnectButtonClicked = useCallback(() => {
    const { is_static, is_api_key, is_paragon } = integrationProvider;
    if (is_static) {
      window.open(
        'https://zapier.com/developer/public-invite/143314/e33e6db24b04cb6e1e272e907a265acf/',
        '_blank',
        'noopener noreferrer'
      );
    } else {
      if (is_api_key) {
        openSlideOver('integration-api-key');
        setShowApiKeyPanel(true);
        return;
      } else if (is_paragon) {
        window.paragon.connect(code, {
          onSuccess: () => {
            window.location.reload();
          },
          onError: (error: any) => {
            console.error('onError', error);
          },
        });
      } else if (!is_static) {
        const receiveMessageFromPopup = (event: any) => {
          // Do we trust the sender of this message? (might be
          // different from what we originally opened, for example).
          const { data, isTrusted } = event;
          if (data && isTrusted && !isFetchingOnProgressRef.current) {
            isFetchingOnProgressRef.current = true;
            fetchIntegrations();
          }

          // if we trust the sender and the source is our popup
          // get the URL params and redirect to our server to use Passport to auth/login
        };

        openPopupWindow(`/oauth/${code}`, `${code}-oauth`, receiveMessageFromPopup);
      }
    }
  }, [code, integrationProvider, openSlideOver]);

  const onApiKeyPanelClosed = useCallback(() => {
    setShowApiKeyPanel(false);
  }, []);

  return (
    <>
      <Helmet>
        <title>Settings | Journey</title>
      </Helmet>
      <div className='mt-md flex items-start'>
        <OrganizationLink to='/integrations'>
          <IconButton>
            <LeftStrongIcon />
          </IconButton>
        </OrganizationLink>
      </div>
      {!subscriptionLoading && isLowerPlanGated ? (
        <PaywallDetail integration={integrationProvider} />
      ) : (
        <>
          <IntegrationDetail
            apiKeysLoaded={isLoaded && !subscriptionLoading}
            integration={integrationProvider}
            onConnectClicked={onConnectButtonClicked}
            apiKeyList={apiKeyList}
            isParagonIntegrationEnabled={isParagonIntegrationEnabled}
            hasApiKey={hasApiKey}
          />
          <DashboardIntegrationsApiKeyPanel
            provider={integrationProvider}
            isOpen={showApiKeyPanel}
            onApiKeyCreate={onApiKeyCreated}
            onClose={onApiKeyPanelClosed}
          />
        </>
      )}
    </>
  );
};
